import React, { useState } from 'react'

import {
  Action,
  AppointmentField,
  DateField,
  DateTimeField,
  FieldSpec,
  LinkedField,
  MultiField,
  PaginatedTable,
  ValueField,
  StatusField,
} from 'src/components/Table'
import { ASN } from 'src/api/models'
import { ASNLabel } from 'src/components/PrintLabel'
import { TransloadPlacard } from 'src/components/PrintLayout'
import { BulkPrint } from 'src/components/Print/Bulk'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import {
  BooleanFilter,
  createMultiQuerySelectFilter,
  createEnumFilter,
  DateTimeFilter,
  FilterHeader,
  MultStringFilter,
} from 'src/modules/shared/components/filters'
import { skuit } from 'src/services/skuit'
import { ListTableProps } from './types'

const fieldOptions: { [key: string]: FieldSpec } = {
  trackingNumber: {
    id: 'trackingNumber',
    label: 'Tracking Number',
    ordering: 'tracking_number',
    renderer: LinkedField(ValueField),
  },
  client: { id: 'client.name', label: 'Client', ordering: 'account__name', renderer: ValueField },
  consignee: { id: 'consignee', label: 'Consignee', renderer: ValueField },
  facility: {
    id: 'facility.name',
    label: 'Facility',
    ordering: 'facility__name',
    renderer: ValueField,
  },
  carrier: {
    id: 'carrier.name',
    label: 'Carrier',
    ordering: 'carrier__name',
    renderer: ValueField,
  },
  status: { id: 'status', label: 'Status', renderer: StatusField },
  transloadNumber: {
    id: 'transloadNumber',
    label: 'Transload #',
    ordering: 'transload__xid',
    renderer: StatusField,
  },
  appointment: {
    id: 'appointment',
    label: 'Appointment',
    ordering: 'appointment__start',
    renderer: AppointmentField,
  },
  estimatedArrivalDate: {
    id: 'estimatedArrivalDate',
    label: 'Estimated Arrival Date',
    ordering: 'estimated_arrival_date',
    renderer: DateField,
  },
  dates: {
    id: 'dates',
    label: 'Dates',
    ordering: 'created_on',
    renderer: MultiField([
      { id: 'createdOn', label: 'Created On', renderer: DateTimeField },
      { id: 'arrivedOn', label: 'Arrived On', renderer: DateTimeField },
      { id: 'receivedOn', label: 'Received On', renderer: DateTimeField },
      { id: 'canceledOn', label: 'Canceled On', renderer: DateTimeField },
    ]),
  },
}

export const ASNListTable = (props: ListTableProps) => {
  const {
    paginator,
    fields,
    extraActions,
    bulkActions,
    exportConfig,
    title = 'Advanced Shipping Notices',
  } = props
  const [selected, setSelected] = useState<ASN[]>([])
  const { print, renderPrintComponent } = useAsyncPrint()

  const allASNsHaveTransload = selected.every((asn) => !!asn.transloadNumber)

  const bulkOptions: { [key: string]: Action } = {
    printASN: {
      label: 'Print ASN',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              onReadyToPrint?.()
              return <ASNLabel asn={obj} />
            }}
          />
        ))
      },
    },
    printTransloadPlacard: {
      label:
        selected.length > 0 && allASNsHaveTransload
          ? 'Print Transload Placard'
          : "Can't print Transload Placard",
      action: () => {
        print((props) => (
          <>
            {allASNsHaveTransload ? (
              <BulkPrint
                {...props}
                items={selected}
                render={({ obj, onReadyToPrint }) => {
                  return <TransloadPlacard onReadyToPrint={onReadyToPrint} asn={obj} />
                }}
              />
            ) : null}
          </>
        ))
      },
    },
    printAll: {
      label: 'Print all',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              return (
                <>
                  <ASNLabel asn={obj} />
                  <TransloadPlacard onReadyToPrint={onReadyToPrint} asn={obj} />
                </>
              )
            }}
          />
        ))
      },
    },
  }

  return (
    <>
      {renderPrintComponent()}
      <PaginatedTable
        title={title}
        fields={fields.map((field) => fieldOptions[field])}
        paginator={paginator}
        onSelectedItem={setSelected}
        actions={extraActions}
        bulkActions={bulkActions?.map((action) => bulkOptions[action])}
        exportConfig={exportConfig}
        filters={[
          {
            id: 'base_header',
            label: 'Base Filters',
            widget: FilterHeader,
          },
          {
            id: 'clients',
            label: 'Clients',
            widget: createMultiQuerySelectFilter(skuit.sc.clients.list),
          },
          {
            id: 'statuses',
            label: 'Status',
            widget: createEnumFilter(ASN.Status),
          },
          {
            id: 'facilities',
            label: 'Facilities',
            widget: createMultiQuerySelectFilter(skuit.sc.facilities.list),
          },
          {
            id: 'carriers',
            label: 'Carriers',
            widget: createMultiQuerySelectFilter(skuit.sc.carriers.list),
          },
          {
            id: 'tracking_numbers',
            label: 'Tracking Numbers',
            widget: MultStringFilter,
          },
          {
            id: 'transloads',
            label: 'Transload Numbers',
            widget: MultStringFilter,
          },

          {
            id: 'scheduling_header',
            label: 'Scheduling',
            widget: FilterHeader,
          },
          {
            id: 'unscheduled',
            label: 'Unscheduled',
            widget: BooleanFilter,
          },
          {
            id: 'scheduled_on_start',
            label: 'Scheduled On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'scheduled_on_end',
            label: 'Scheduled On or Before',
            widget: DateTimeFilter,
          },

          {
            id: 'created_header',
            label: 'Created',
            widget: FilterHeader,
          },
          {
            id: 'created_on_start',
            label: 'Created On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'created_on_end',
            label: 'Created On or Before',
            widget: DateTimeFilter,
          },

          {
            id: 'received_header',
            label: 'Received',
            widget: FilterHeader,
          },
          {
            id: 'received_on_start',
            label: 'Received On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'received_on_end',
            label: 'Received On or Before',
            widget: DateTimeFilter,
          },

          {
            id: 'canceled_header',
            label: 'Canceled',
            widget: FilterHeader,
          },
          {
            id: 'canceled_on_start',
            label: 'Canceled On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'canceled_on_end',
            label: 'Canceled On or Before',
            widget: DateTimeFilter,
          },
        ]}
      />
    </>
  )
}
